'use client'

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  assignCaseAdvisor,
  claimCaseAdvisor,
  createCase,
  publishCase,
  saveInvestorProfile,
  sendCaseLoginDetails,
  unassignCaseAdvisor,
  updateCase,
  updateCaseStatus,
  updateCaseSeriousness,
  type CreateCaseInput,
  type UpdateCaseInput,
} from '@/features/cases/api/cases.api'
import {
  CASE_STATUS_LABEL,
  type CaseStatus,
  type SaveInvestorProfileInput,
  type SeriousnessStatus,
} from '@/features/cases/types'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

/** `queryKeys.cases.list(params)` embeds the full filter object, so there's no single exact key
 * for "every open cases list regardless of filters". Matching on the shared
 * `['cases', 'real', 'list', ...]` prefix invalidates every open list view (staff's and admin's)
 * without over-invalidating the unrelated mock-era `cases` keys. */
function invalidateCasesLists(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'cases' && q.queryKey[1] === 'real' && q.queryKey[2] === 'list',
  })
}

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

export function useCreateCase() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCaseInput) => createCase(authedFetch, input),
    onSuccess: (created) => {
      invalidateCasesLists(queryClient)
      toast({ variant: 'success', title: `Case ${created.reference} created` })
    },
    onError: toastError,
  })
}

export function useUpdateCase(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateCaseInput) => updateCase(authedFetch, caseId, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.cases.real(caseId), updated)
      invalidateCasesLists(queryClient)
      toast({ variant: 'success', title: 'Case details saved' })
    },
    onError: toastError,
  })
}

export function useUpdateCaseStatus(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (status: CaseStatus) => updateCaseStatus(authedFetch, caseId, status),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.cases.real(caseId), updated)
      invalidateCasesLists(queryClient)
      toast({
        variant: 'success',
        title: `Status moved to ${CASE_STATUS_LABEL[updated.caseStatus]}`,
      })
    },
    onError: toastError,
  })
}

export function useUpdateCaseSeriousness() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    // `previousStatus` comes from the caller (the list row already has the case's current
    // `caseStatus` in scope) rather than a query-cache lookup — the row that renders this Select
    // is fed from the *list* cache (`queryKeys.cases.list(params)`), not the detail cache
    // (`queryKeys.cases.real(id)`), which usually has no entry yet for a case nobody has opened.
    mutationFn: ({
      caseId,
      seriousnessStatus,
    }: {
      caseId: string
      seriousnessStatus: SeriousnessStatus
      previousStatus: CaseStatus
    }) => updateCaseSeriousness(authedFetch, caseId, seriousnessStatus),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData(queryKeys.cases.real(updated.id), updated)
      invalidateCasesLists(queryClient)
      // Surfaces the qualify-side-effect (api-design.md §4.5: qualifying from submitted/
      // consultation_scheduled force-advances the pipeline) instead of a confirm dialog gating it.
      const advanced =
        updated.caseStatus === 'consultation_completed' &&
        (variables.previousStatus === 'submitted' ||
          variables.previousStatus === 'consultation_scheduled')
      toast({
        variant: 'success',
        title: advanced
          ? 'Lead qualified — status advanced to Consultation Completed'
          : 'Lead status updated',
      })
    },
    onError: toastError,
  })
}

// No `caseId` param on either hook — unlike most mutations here, the caller is a *list* row
// (admin-cases-view.tsx), not a single-case detail screen, so the mutation variables carry the id.
// Mirrors `useUpdateCaseSeriousness`'s exact shape for the same reason.
export function useAssignAdvisor() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, staffId }: { caseId: string; staffId: string }) =>
      assignCaseAdvisor(authedFetch, caseId, staffId),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.cases.real(updated.id), updated)
      invalidateCasesLists(queryClient)
      toast({ variant: 'success', title: `Assigned to ${updated.advisor?.name ?? 'advisor'}` })
    },
    onError: toastError,
  })
}

export function useUnassignAdvisor() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (caseId: string) => unassignCaseAdvisor(authedFetch, caseId),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.cases.real(updated.id), updated)
      invalidateCasesLists(queryClient)
      toast({ variant: 'success', title: 'Unassigned' })
    },
    onError: toastError,
  })
}

/** Staff self-claim — a separate hook from `useAssignAdvisor` (admin-only). On a lost race (409,
 * someone else claimed it first), still invalidates both list views so the now-stale row disappears
 * from "Unassigned" without the caller needing a second failed click to notice. */
export function useClaimCase() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (caseId: string) => claimCaseAdvisor(authedFetch, caseId),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.cases.real(updated.id), updated)
      invalidateCasesLists(queryClient)
      toast({ variant: 'success', title: 'Case claimed' })
    },
    onError: (error) => {
      invalidateCasesLists(queryClient)
      toastError(error)
    },
  })
}

export function useSaveInvestorProfile(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveInvestorProfileInput) =>
      saveInvestorProfile(authedFetch, caseId, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.cases.real(caseId), updated)
      invalidateCasesLists(queryClient)
      toast({ variant: 'success', title: 'Profile saved' })
    },
    onError: toastError,
  })
}

export function usePublishCase(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => publishCase(authedFetch, caseId),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.cases.real(caseId), updated)
      invalidateCasesLists(queryClient)
      toast({
        variant: 'success',
        title: 'Case published — the investor can now sign in once you send their credentials',
      })
    },
    onError: toastError,
  })
}

// Deliberately doesn't follow the toast-in-hook template above — the response carries no
// `CaseDetail` to cache, and the toast copy depends on `emailSent`, which only the caller (the
// confirm dialog) knows how to phrase. `retry: 0` because a retried send would attempt a second
// password rotation from one click. Mirrors `use-admin-users.ts`'s `useSendLoginDetails` shape.
export function useSendCaseLoginDetails(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => sendCaseLoginDetails(authedFetch, caseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      invalidateCasesLists(queryClient)
    },
    retry: 0,
  })
}
